// The brand in the sidebar doubles as the workspace switcher: JobNimbus CD
// (deployments) or JobNimbus Infra (terraform stacks and modules).

import React, { useState } from 'react';
import { Box, ButtonBase, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Check as CheckIcon,
  Cloud as CloudIcon,
  ExpandMore as ExpandIcon,
  Hub as InfraIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getColors, rounded } from '../theme/tokens';
import { transition } from '../theme/motion';
import { Workspace, workspaceFor } from './SidebarNav';

interface WorkspaceMeta {
  id: Workspace;
  name: string;
  description: string;
  home: string;
  icon: React.ElementType;
}

export const WORKSPACES: WorkspaceMeta[] = [
  { id: 'cd', name: 'JobNimbus CD', description: 'Deployments across apps and environments', home: '/', icon: CloudIcon },
  { id: 'infra', name: 'JobNimbus Infra', description: 'Terraform stacks, modules and versions', home: '/infra', icon: InfraIcon },
];

const WorkspaceSwitcher: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = WORKSPACES.find(w => w.id === workspaceFor(pathname)) || WORKSPACES[0];
  const Icon = current.icon;

  return (
    <>
      <ButtonBase
        onClick={e => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label={`${current.name}, switch workspace`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: compact ? 0.75 : 1,
          py: 0.5,
          mx: compact ? 0 : -1,
          borderRadius: `${rounded.sm}px`,
          transition: transition('background-color'),
          '&:hover': { backgroundColor: c.hoverWash },
        }}
      >
        <Icon sx={{ fontSize: compact ? 18 : 20, color: c.primary }} />
        <Typography
          sx={{ fontWeight: 700, fontSize: compact ? '0.95rem' : '1rem', letterSpacing: '-0.25px', color: c.ink }}
        >
          {current.name}
        </Typography>
        <ExpandIcon sx={{ fontSize: 18, color: c.inkMuted }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 280, mt: 0.5 } } }}
      >
        {WORKSPACES.map(w => {
          const WIcon = w.icon;
          const active = w.id === current.id;
          return (
            <MenuItem
              key={w.id}
              selected={active}
              onClick={() => {
                setAnchor(null);
                if (!active) navigate(w.home);
              }}
            >
              <ListItemIcon>
                <WIcon sx={{ fontSize: 20, color: c.primary }} />
              </ListItemIcon>
              <ListItemText
                primary={w.name}
                secondary={w.description}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.78rem' }}
              />
              {active && (
                <Box sx={{ ml: 1.5, display: 'flex' }}>
                  <CheckIcon sx={{ fontSize: 18, color: c.primary }} />
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default WorkspaceSwitcher;
